(() => {
  'use strict';

  // Observe native image requests; no fetch, eager catalog preload or purchase side effects.
  function createImageFeedback({ status, sourceURL, focusAfterRetry = () => {} }) {
    if (!status) return { watch() {}, reset() {}, destroy() {} };
    const message = status.querySelector('[data-zp-image-message]');
    const actions = status.querySelector('[data-zp-image-actions]');
    const retry = status.querySelector('[data-zp-image-retry]');
    const original = status.querySelector('[data-zp-image-original]');
    const lifetime = new AbortController();
    let request = null;
    let active = null;
    let sequence = 0;
    let stopped = false;
    const clearImageState = (image) => {
      image?.removeAttribute('data-zp-image-state');
      image?.removeAttribute('aria-busy');
    };
    const reset = () => {
      sequence += 1;
      request?.abort();
      request = null;
      clearImageState(active?.image);
      active = null;
      status.hidden = true;
      status.setAttribute('data-zp-feedback-state', 'idle');
      if (message) message.textContent = '';
      if (actions) actions.hidden = true;
      original?.removeAttribute('href');
    };
    const paint = (state) => {
      if (!active || stopped) return;
      active.state = state;
      active.image.setAttribute('data-zp-image-state', state);
      active.image.setAttribute('aria-busy', String(state === 'loading'));
      status.hidden = state === 'ready';
      status.setAttribute('data-zp-feedback-state', state);
      if (message) message.textContent = state === 'loading' ? 'Loading image…' : state === 'error' ? 'This image could not be loaded. Try again or open the original image.' : 'Image loaded.';
      if (actions) actions.hidden = state !== 'error';
      if (retry) retry.disabled = state !== 'error';
    };
    const watch = (image, { fullSource = '', exactSource = false } = {}) => {
      reset();
      if (!image || stopped) return;
      const source = sourceURL(fullSource || image.getAttribute('src'));
      if (!source) return;
      const token = sequence;
      const fingerprint = ['src', 'srcset', 'sizes'].map((key) => image.getAttribute(key));
      active = { image, source, exactSource, state: 'loading' };
      request = new AbortController();
      if (original) original.setAttribute('href', source);
      const current = () => !stopped && token === sequence && active?.image === image
        && ['src', 'srcset', 'sizes'].every((key, index) => image.getAttribute(key) === fingerprint[index]);
      const correctRequest = () => !exactSource || !image.currentSrc || sourceURL(image.currentSrc) === source;
      const loaded = () => {
        // A delayed event from a previous URL must not clear the current request's status.
        if (current() && correctRequest() && image.complete && image.naturalWidth > 0) paint('ready');
      };
      const failed = () => {
        if (current() && correctRequest() && image.complete && !image.naturalWidth) paint('error');
      };
      image.addEventListener('load', loaded, { signal: request.signal });
      image.addEventListener('error', failed, { signal: request.signal });
      paint('loading');
      if (image.complete) image.naturalWidth > 0 ? loaded() : failed();
    };
    retry?.addEventListener('click', () => {
      if (!active || active.state !== 'error' || stopped) return;
      const { image, source, exactSource } = active;
      const attributes = ['sizes', 'srcset', 'src'].map((key) => [key, image.getAttribute(key)]);
      focusAfterRetry(image);
      reset();
      // Retry the same native URL, preserving responsive candidates and without cache-busting.
      image.removeAttribute('srcset');
      image.removeAttribute('src');
      attributes.forEach(([key, value]) => { if (value !== null) image.setAttribute(key, value); });
      watch(image, { fullSource: source, exactSource });
    }, { signal: lifetime.signal });
    return { watch, reset, destroy() { reset(); stopped = true; lifetime.abort(); } };
  }

  if (typeof window === 'undefined') {
    if (typeof module !== 'undefined' && module.exports) module.exports = { createImageFeedback };
    return;
  }

  // A section may include this asset more than once in the theme editor.
  if (window.__ziqingProductController) {
    window.__ziqingProductController.init(document);
    return;
  }

  const instances = new WeakMap();
  const rootsWithin = (scope) => [
    ...(scope.matches?.('[data-zp-root]') ? [scope] : []),
    ...scope.querySelectorAll('[data-zp-root]'),
  ];
  const focus = (element) => {
    if (element?.isConnected) element.focus({ preventScroll: true });
  };
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const imageSource = (value) => {
    try {
      const url = new URL(value || '', window.location.href);
      return value && ['https:', 'http:'].includes(url.protocol) ? url.href : '';
    } catch (_) { return ''; }
  };

  const initRoot = (root) => {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const listen = (element, type, handler, options = {}) => element?.addEventListener(type, handler, { ...options, signal: controller.signal });
    const own = (selector) => [...root.querySelectorAll(selector)].filter((element) => element.closest('[data-zp-root]') === root);
    const one = (selector) => own(selector)[0];
    let destroyed = false;

    const panels = own('[data-zp-media]');
    const thumbs = own('[data-zp-thumb]');
    const stage = one('[data-zp-stage]');
    const galleryFeedback = createImageFeedback({
      status: one('[data-zp-image-status="gallery"]'), sourceURL: imageSource,
      focusAfterRetry: (image) => focus(image.closest('[data-zp-zoom]')),
    });
    let mediaIndex = Math.max(0, panels.findIndex((panel) => !panel.hidden));
    const iframeSources = new Map();
    panels.forEach((panel) => panel.querySelectorAll('iframe').forEach((frame) => {
      const original = frame.getAttribute('data-zp-iframe-src') || frame.getAttribute('src');
      let source = '';
      try {
        const url = new URL(original || '', window.location.href);
        if (original && url.protocol === 'https:') {
          url.searchParams.set('autoplay', '0');
          source = url.href;
        }
      } catch (_) { /* Invalid external media must stay unloaded. */ }
      iframeSources.set(frame, source);
      if (source) frame.setAttribute('data-zp-iframe-src', source);
      else frame.removeAttribute('data-zp-iframe-src');
      frame.removeAttribute('src');
    }));
    const pausePanel = (panel) => {
      panel?.querySelectorAll('video').forEach((video) => {
        try { video.pause(); } catch (_) { /* A media element may not be loaded. */ }
      });
      panel?.querySelectorAll('iframe').forEach((frame) => frame.removeAttribute('src'));
    };
    const restorePanel = (panel) => panel?.querySelectorAll('iframe').forEach((frame) => {
      const source = iframeSources.get(frame);
      if (source && frame.getAttribute('src') !== source) frame.setAttribute('src', source);
    });
    const showMedia = (index, focusThumb = false, scrollThumb = false) => {
      if (!panels.length) return;
      const nextIndex = (index + panels.length) % panels.length;
      panels.forEach((panel, position) => {
        if (position !== nextIndex) pausePanel(panel);
        panel.hidden = position !== nextIndex;
      });
      mediaIndex = nextIndex;
      restorePanel(panels[mediaIndex]);
      const activeImage = panels[mediaIndex].querySelector('[data-zp-zoom] img');
      if (activeImage) galleryFeedback.watch(activeImage, { fullSource: activeImage.closest('[data-zp-zoom]')?.dataset.full });
      else galleryFeedback.reset();
      const id = panels[mediaIndex].dataset.zpMedia;
      let activeThumb;
      thumbs.forEach((thumb) => {
        const selected = thumb.dataset.zpThumb === id;
        thumb.setAttribute('aria-pressed', String(selected));
        thumb.tabIndex = selected ? 0 : -1;
        thumb.classList.toggle('is-active', selected);
        if (selected) activeThumb = thumb;
      });
      own('[data-zp-count]').forEach((counter) => { counter.textContent = `${mediaIndex + 1} / ${panels.length}`; });
      if (scrollThumb) activeThumb?.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
      if (focusThumb) focus(activeThumb);
    };
    thumbs.forEach((thumb) => {
      const index = panels.findIndex((panel) => panel.dataset.zpMedia === thumb.dataset.zpThumb);
      thumb.disabled = index < 0;
      listen(thumb, 'click', () => { if (index >= 0) showMedia(index, false, true); });
      listen(thumb, 'keydown', (event) => {
        const destination = { ArrowLeft: mediaIndex - 1, ArrowRight: mediaIndex + 1, Home: 0, End: panels.length - 1 }[event.key];
        if (destination === undefined || !panels.length) return;
        event.preventDefault();
        showMedia(destination, true, true);
      });
    });
    [[one('[data-zp-prev]'), -1], [one('[data-zp-next]'), 1]].forEach(([button, direction]) => {
      if (button) button.disabled = panels.length < 2;
      listen(button, 'click', () => showMedia(mediaIndex + direction, false, true));
    });
    let gesture = null;
    let suppressZoomUntil = 0;
    const interactiveMedia = 'button, a, input, select, textarea, video, iframe, model-viewer, [contenteditable="true"]';
    const blocksSwipe = (target) => {
      const control = target.closest(interactiveMedia);
      // The main image itself is a zoom button: allow its drag, but not its click afterwards.
      return control && !(control.hasAttribute('data-zp-zoom') && control.closest('[data-zp-root]') === root);
    };
    listen(stage, 'pointerdown', (event) => {
      if (event.pointerType !== 'touch' || event.isPrimary === false || blocksSwipe(event.target)) { gesture = null; return; }
      gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, started: Date.now() };
    }, { passive: true });
    listen(stage, 'pointerup', (event) => {
      const start = gesture;
      gesture = null;
      if (!start || start.id !== event.pointerId || blocksSwipe(event.target)) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy) * 1.5 && Date.now() - start.started < 900) {
        suppressZoomUntil = Date.now() + 450;
        showMedia(mediaIndex + (dx < 0 ? 1 : -1), false, true);
      }
    }, { passive: true });
    listen(stage, 'pointercancel', () => { gesture = null; });
    showMedia(mediaIndex);

    const dialog = one('[data-zp-lightbox]');
    const lightboxImage = one('[data-zp-lightbox-image]');
    const lightboxFeedback = createImageFeedback({
      status: one('[data-zp-image-status="lightbox"]'), sourceURL: imageSource,
      focusAfterRetry: () => focus(one('[data-zp-lightbox-close]')),
    });
    const zooms = own('[data-zp-zoom]').map((button) => ({
      button,
      panel: panels.find((panel) => panel.dataset.zpMedia === button.dataset.zpZoom),
      source: imageSource(button.dataset.full),
    })).filter((item) => item.panel && item.source);
    let lightboxIndex = 0;
    let returnTarget = null;
    const lightboxButtons = [one('[data-zp-lightbox-prev]'), one('[data-zp-lightbox-next]')];
    lightboxButtons.forEach((button) => { if (button) button.disabled = zooms.length < 2; });
    const showLightboxImage = (index) => {
      if (!lightboxImage || !zooms.length) return;
      lightboxIndex = (index + zooms.length) % zooms.length;
      const item = zooms[lightboxIndex];
      lightboxImage.removeAttribute('srcset');
      lightboxImage.removeAttribute('sizes');
      lightboxImage.setAttribute('src', item.source);
      lightboxImage.alt = item.panel.querySelector('img')?.alt || '';
      lightboxFeedback.watch(lightboxImage, { fullSource: item.source, exactSource: true });
      own('[data-zp-lightbox-count]').forEach((counter) => { counter.textContent = `${lightboxIndex + 1} / ${zooms.length}`; });
      // Returning from zoom lands on the image that was actually inspected.
      showMedia(panels.indexOf(item.panel));
    };
    const finishLightbox = () => {
      lightboxFeedback.reset();
      lightboxImage?.removeAttribute('src');
      lightboxImage?.removeAttribute('srcset');
      if (destroyed) return;
      const current = zooms[lightboxIndex];
      const target = returnTarget && !returnTarget.closest('[hidden]') ? returnTarget : current?.button;
      focus(target || thumbs.find((thumb) => thumb.dataset.zpThumb === panels[mediaIndex]?.dataset.zpMedia));
      returnTarget = null;
    };
    const closeLightbox = () => {
      if (dialog?.open) dialog.close();
      else finishLightbox();
    };
    own('[data-zp-zoom]').forEach((button) => {
      const index = zooms.findIndex((item) => item.button === button);
      if (index < 0 || !dialog || !lightboxImage || typeof dialog.showModal !== 'function') { button.disabled = true; return; }
      listen(button, 'click', (event) => {
        if (Date.now() < suppressZoomUntil) { event.preventDefault(); return; }
        if (dialog.open) return;
        returnTarget = button;
        panels.forEach(pausePanel);
        showLightboxImage(index);
        dialog.showModal();
        focus(one('[data-zp-lightbox-close]'));
      });
    });
    lightboxButtons.forEach((button, index) => listen(button, 'click', () => showLightboxImage(lightboxIndex + (index ? 1 : -1))));
    listen(one('[data-zp-lightbox-close]'), 'click', closeLightbox);
    listen(dialog, 'close', finishLightbox);
    listen(dialog, 'cancel', (event) => { event.preventDefault(); closeLightbox(); });
    listen(dialog, 'click', (event) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeLightbox();
    });
    listen(dialog, 'keydown', (event) => {
      if (event.key === 'Escape') { event.stopPropagation(); return; }
      const destination = { ArrowLeft: lightboxIndex - 1, ArrowRight: lightboxIndex + 1, Home: 0, End: zooms.length - 1 }[event.key];
      if (destination === undefined || !dialog.open) return;
      event.preventDefault();
      event.stopPropagation();
      showLightboxImage(destination);
    });

    const variantControl = one('[data-zp-variant]');
    const quantity = one('[data-zp-quantity]');
    const addButton = one('[data-zp-add]');
    const payment = one('[data-zp-payment]');
    const minus = one('[data-zp-minus]');
    const plus = one('[data-zp-plus]');
    const purchaseForm = variantControl?.closest('form');
    const contactOnly = root.dataset.zpPurchaseMode === 'contact';
    const addLabel = addButton?.dataset.labelAvailable || 'Add to cart';
    let variants = [];
    try {
      const value = JSON.parse(one('[data-zp-variants]')?.textContent || '[]');
      if (Array.isArray(value)) variants = value.filter((variant) => variant && typeof variant.id === 'string' && variant.id && typeof variant.available === 'boolean' && typeof variant.price === 'string' && typeof variant.comparePrice === 'string');
    } catch (_) { /* Invalid variant data must never enable a purchase. */ }
    let selectedVariant = null;
    const quantityValid = () => !quantity || (quantity.value !== '' && quantity.checkValidity());
    const updatePurchase = () => {
      const available = selectedVariant?.available === true && !contactOnly;
      const valid = quantityValid();
      if (addButton) {
        addButton.disabled = !available || !valid;
        addButton.textContent = contactOnly ? 'Contact us for purchase options' : selectedVariant ? (available ? addLabel : 'Sold out') : 'Unavailable';
      }
      if (payment) payment.hidden = !available || !valid;
      if (quantity) {
        const value = quantity.valueAsNumber;
        const minimum = Number(quantity.min);
        const maximum = quantity.max === '' ? Infinity : Number(quantity.max);
        const step = Number(quantity.step);
        if (minus) minus.disabled = !available || (Number.isFinite(value) && value <= minimum);
        if (plus) plus.disabled = !available || (Number.isFinite(value) && value + step > maximum);
      }
    };
    const setVariant = (updateUrl = false) => {
      selectedVariant = variants.find((variant) => variant.id === String(variantControl?.value || '')) || null;
      own('[data-zp-selected-option]').forEach((element) => { element.textContent = selectedVariant ? (variantControl?.selectedOptions?.[0]?.textContent || '') : 'Unavailable'; });
      own('[data-zp-price]').forEach((element) => { element.textContent = selectedVariant?.price || '—'; });
      own('[data-zp-compare-price]').forEach((element) => {
        element.textContent = selectedVariant?.comparePrice || '';
        element.hidden = !selectedVariant?.comparePrice;
      });
      own('[data-zp-stock]').forEach((element) => { element.textContent = contactOnly ? 'Contact us for purchase options' : selectedVariant ? (selectedVariant.available ? 'Available' : 'Sold out') : 'Unavailable'; });
      if (quantity && selectedVariant) {
        const min = Number.isInteger(selectedVariant.min) && selectedVariant.min > 0 ? selectedVariant.min : 1;
        const step = Number.isInteger(selectedVariant.step) && selectedVariant.step > 0 ? selectedVariant.step : 1;
        const max = Number.isInteger(selectedVariant.max) && selectedVariant.max >= 0 ? selectedVariant.max : null;
        quantity.min = String(min);
        quantity.step = String(step);
        quantity.required = true;
        if (max === null) quantity.removeAttribute('max');
        else quantity.max = String(max);
        // A new option may have different quantity rules. Manual edits keep native validity.
        if (!quantityValid()) quantity.value = String(min);
      }
      const variantMedia = panels.findIndex((panel) => panel.dataset.zpMedia === selectedVariant?.mediaId);
      if (variantMedia >= 0) showMedia(variantMedia, false, updateUrl);
      if (updateUrl && selectedVariant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', selectedVariant.id);
        try { window.history.replaceState(window.history.state, '', url.href); } catch (_) { /* Embedded previews may restrict history. */ }
      }
      updatePurchase();
    };
    listen(variantControl, 'change', () => setVariant(true));
    listen(quantity, 'input', updatePurchase);
    listen(quantity, 'change', updatePurchase);
    [[minus, -1], [plus, 1]].forEach(([button, direction]) => listen(button, 'click', () => {
      if (!quantity || button.disabled) return;
      try { direction > 0 ? quantity.stepUp() : quantity.stepDown(); } catch (_) { return; }
      quantity.dispatchEvent(new Event('input', { bubbles: true }));
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    }));
    listen(purchaseForm, 'submit', (event) => {
      // Keep Shopify's native POST and payment controls; do not fabricate a cart state.
      if (contactOnly || !selectedVariant?.available || String(variantControl.value) !== selectedVariant.id || !quantityValid()) {
        event.preventDefault();
        if (quantity && !quantityValid()) { quantity.reportValidity(); focus(quantity); }
        else focus(variantControl);
      }
    });
    setVariant();

    const nav = one('[data-zp-nav]');
    const targets = own('[id]');
    const anchors = nav ? [...nav.querySelectorAll('a[href^="#"]')].map((link) => {
      let id;
      try { id = decodeURIComponent(link.getAttribute('href').slice(1)); } catch (_) { return null; }
      const target = targets.find((element) => element.id === id);
      return target ? { link, target } : null;
    }).filter(Boolean) : [];
    const activateAnchor = (active) => anchors.forEach(({ link }) => {
      if (link === active) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
    const updateAnchor = () => {
      if (!anchors.length || destroyed) return;
      const line = (nav.offsetHeight || 56) + 24;
      let active = anchors[0];
      anchors.forEach((anchor) => {
        // Match scrollIntoView's CSS landing position, including fractional pixel rounding.
        const margin = parseFloat(window.getComputedStyle(anchor.target).scrollMarginTop) || 0;
        if (anchor.target.getBoundingClientRect().top <= Math.max(line, margin + 2)) active = anchor;
      });
      activateAnchor(active.link);
    };
    anchors.forEach(({ link, target }) => listen(link, 'click', (event) => {
      if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      const url = new URL(window.location.href);
      url.hash = target.id;
      try { window.history.replaceState(window.history.state, '', url.href); } catch (_) { /* Preview context may restrict history. */ }
      target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
      focus(target);
      activateAnchor(link);
    }));
    // The provider badge uses its own fixed scroll offset, which can hide the
    // review heading behind our responsive sticky navigation. Route only this
    // entry through the same accessible anchor path as the Reviews nav link.
    const reviewAnchor = anchors.find(({ target }) => target.classList.contains('zp-reviews'));
    if (reviewAnchor) listen(root, 'click', (event) => {
      if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!event.target.closest?.('.jdgm-preview-badge')) return;
      event.preventDefault();
      event.stopPropagation();
      reviewAnchor.link.click();
    }, { capture: true });
    let observer = null;
    let scrollFrame = null;
    if (anchors.length) {
      if (typeof IntersectionObserver === 'function') {
        observer = new IntersectionObserver(updateAnchor, { rootMargin: '-56px 0px -60% 0px', threshold: [0, 1] });
        anchors.forEach(({ target }) => observer.observe(target));
      }
      // Covers gaps between sections as well as the no-IntersectionObserver fallback.
      listen(window, 'scroll', () => {
        if (scrollFrame !== null) return;
        scrollFrame = window.requestAnimationFrame(() => { scrollFrame = null; updateAnchor(); });
      }, { passive: true });
      listen(window, 'resize', updateAnchor);
      updateAnchor();
    }

    instances.set(root, () => {
      destroyed = true;
      controller.abort();
      observer?.disconnect();
      if (scrollFrame !== null) window.cancelAnimationFrame(scrollFrame);
      panels.forEach(pausePanel);
      galleryFeedback.destroy();
      lightboxFeedback.destroy();
      if (dialog?.open) dialog.close();
      lightboxImage?.removeAttribute('src');
      lightboxImage?.removeAttribute('srcset');
      instances.delete(root);
    });
  };

  const init = (scope) => rootsWithin(scope).forEach(initRoot);
  window.__ziqingProductController = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => rootsWithin(event.target).forEach((root) => instances.get(root)?.()));
})();
